<template>
  <div class="flex flex-col gap-2 filter-input">
    <!-- Header for date presets (no operator dropdown, show "equals to" for presets) -->
    <div
      v-if="useDatePresets && !showCustomDatePicker"
      class="flex items-center justify-between px-3"
    >
      <div class="flex items-center gap-1 flex-1 min-w-0">
        <span
          class="text-sm font-semibold text_color truncate max-w-[150px]"
          :title="filterName"
          >{{ filterName }}</span
        >
        <span class="text-sm secondary_color px-1">equals to</span>
      </div>
      <slot name="delete-icon"></slot>
    </div>

    <!-- Header for custom date picker with operators -->
    <div
      v-else-if="useDatePresets && showCustomDatePicker && showOperators"
      class="flex items-center justify-between px-3"
    >
      <div class="flex items-center gap-1 flex-1 min-w-0">
        <span
          class="text-sm font-semibold text_color truncate max-w-[150px]"
          :title="filterName"
          >{{ filterName }}</span
        >
        <FieldDropdown
          ref="operatorDropdownRef"
          :field="{
            name: filterName,
            type: 'select',
            options: operatorOptions,
          }"
          v-model="selectedOperator"
          :show-search="false"
          :show-arrow="true"
          variant="2"
          max-height="600px"
          @update:modelValue="handleOperatorChange"
        />
      </div>
      <slot name="delete-icon"></slot>
    </div>

    <!-- Header for custom date picker without operators (just filter name) -->
    <div
      v-else-if="useDatePresets && showCustomDatePicker"
      class="flex items-center justify-between px-3"
    >
      <div class="flex items-center gap-1 flex-1 min-w-0">
        <span
          class="text-sm font-semibold text_color truncate max-w-[150px]"
          :title="filterName"
          >{{ filterName }}</span
        >
      </div>
      <slot name="delete-icon"></slot>
    </div>

    <!-- Header with operators (for non-date-preset filters) -->
    <div
      v-else-if="showOperators"
      class="flex items-center justify-between px-3"
    >
      <div class="flex items-center gap-1 flex-1 min-w-0">
        <span
          class="text-sm font-semibold text_color truncate max-w-[150px]"
          :title="filterName"
          >{{ filterName }}</span
        >
        <FieldDropdown
          ref="operatorDropdownRef"
          :field="{
            name: filterName,
            type: 'select',
            options: operatorOptions,
          }"
          v-model="selectedOperator"
          :show-search="false"
          :show-arrow="true"
          variant="2"
          max-height="600px"
          @update:modelValue="handleOperatorChange"
        />
      </div>
      <slot name="delete-icon"></slot>
    </div>

    <!-- Header without operators -->
    <div v-else class="flex items-center justify-between p-3 pt-1">
      <span class="text-sm font-semibold text_color">{{ filterName }}</span>
      <slot name="delete-icon"></slot>
    </div>

    <div v-if="isRecordFieldComputed" class="pb-3">
      <RecordTypeFilterInput
        ref="recordTypeFilterRef"
        :filter-id="filterId"
        :filter-name="filterName"
        :current-value="currentValue"
        :attribute="attribute"
        :related-objects="relatedObjects"
        :show-operators="showOperators"
        :selected-operator="selectedOperator"
        @submit="(data) => $emit('submit', data)"
        @clear="(data) => $emit('clear', data)"
      />
    </div>
    <!-- Date presets for DynamicCRM (separate section, not dependent on showOperators) -->
    <div v-if="useDatePresets" class="px-3 pb-3">
      <!-- Show date picker when custom is selected -->
      <div v-if="showCustomDatePicker" class="flex flex-col gap-2">
        <div
          class="flex items-center gap-1 cursor-pointer text_color hover:opacity-80"
          @click.stop="handleBackToPresets"
        >
          <ThemeImage
            :icon="imageNames.CHEVRON_ARROW_LEFT_GREY"
            alt="back_icon"
            class="size-4"
          />
          <span class="text-sm">Back</span>
        </div>
        <!-- Show date pickers only if operator requires a value -->
        <template v-if="showDatePresetDateInput">
          <!-- Show two date pickers for between/not_between operators -->
          <template v-if="showDatePresetBetweenInputs">
            <div class="flex gap-2">
              <a-date-picker
                v-model:value="customDateValue"
                placeholder="Start date"
                class="flex-1"
                :show-time="isDateTimeField"
                :get-popup-container="getPopupContainer"
                :popup-style="datePickerPopupStyle"
                @change="handleCustomDateRangeChange"
                @click.stop=""
              />
              <a-date-picker
                v-model:value="customDateValue2"
                placeholder="End date"
                class="flex-1"
                :show-time="isDateTimeField"
                :disabled-date="disabledCustomEndDate"
                :disabled-time="
                  isDateTimeField ? disabledCustomEndTime : undefined
                "
                :get-popup-container="getPopupContainer"
                :popup-style="datePickerPopupStyle"
                @change="handleCustomDateRangeChange"
                @click.stop=""
              />
            </div>
          </template>
          <!-- Show single date picker for other operators -->
          <a-date-picker
            v-else
            v-model:value="customDateValue"
            :placeholder="`Select ${filterName.toLowerCase()}`"
            class="w-full"
            :show-time="isDateTimeField"
            :get-popup-container="getPopupContainer"
            :popup-style="datePickerPopupStyle"
            @change="handleCustomDateChange"
            @click.stop=""
          />
        </template>
      </div>
      <!-- Show date preset options -->
      <div v-else class="max-h-64 overflow-auto py-2">
        <div
          v-for="option in props.datePresetOptions"
          :key="option.id"
          class="dropdown_item flex items-center gap-2 px-2 py-2 cursor-pointer rounded"
          :class="{ selected: isDatePresetSelected(option.id) }"
          @click.stop="handleDatePresetClick(option)"
        >
          <ThemeImage
            v-if="isDatePresetSelected(option.id)"
            :icon="imageNames.TICK_GREY"
            alt="tick_icon"
            class="size-4"
          />
          <div v-else class="size-4 shrink-0" />
          <span class="text_color text-sm truncate">{{ option.name }}</span>
        </div>
      </div>
    </div>

    <div v-else-if="showOperators && showValueInput" class="px-3 pb-3">
      <template v-if="showBetweenInputs">
        <div class="flex gap-2">
          <a-date-picker
            v-if="isDateFieldComputed"
            v-model:value="localValue"
            placeholder="Start date"
            class="flex-1"
            :show-time="isDateTimeField"
            :get-popup-container="getPopupContainer"
            :popup-style="datePickerPopupStyle"
            @change="handleDateChange"
            @click.stop=""
          />
          <a-input
            v-else
            v-model:value="localValue"
            placeholder="Min value"
            :type="isNumericFieldComputed ? 'number' : 'text'"
            allow-clear
            auto-focus
            @keydown.enter="handleSubmit"
            @click.stop=""
            @keydown.stop="$event.stopPropagation()"
            class="input_medium flex-1"
          >
            <template v-if="attribute?.displayPrefix" #prefix>
              <span class="secondary_color text-sm">{{ attribute.displayPrefix.toUpperCase() }}-</span>
            </template>
          </a-input>
          <a-date-picker
            v-if="isDateFieldComputed"
            v-model:value="localValue2"
            placeholder="End date"
            class="flex-1"
            :show-time="isDateTimeField"
            :disabled-date="disabledBetweenEndDate"
            :disabled-time="
              isDateTimeField ? disabledBetweenEndTime : undefined
            "
            :get-popup-container="getPopupContainer"
            :popup-style="datePickerPopupStyle"
            @change="handleDateChange"
            @click.stop=""
          />
          <a-input
            v-else
            v-model:value="localValue2"
            placeholder="Max value"
            :type="isNumericFieldComputed ? 'number' : 'text'"
            :min="
              isNumericFieldComputed && localValue
                ? Number(localValue)
                : undefined
            "
            allow-clear
            @keydown.enter="handleSubmit"
            @click.stop=""
            @keydown.stop="$event.stopPropagation()"
            class="input_medium flex-1"
          >
            <template v-if="attribute?.displayPrefix" #prefix>
              <span class="secondary_color text-sm">{{ attribute.displayPrefix.toUpperCase() }}-</span>
            </template>
          </a-input>
        </div>
      </template>

      <a-date-picker
        v-else-if="isDateFieldComputed"
        v-model:value="localValue"
        :placeholder="`Select ${filterName.toLowerCase()}`"
        class="w-full"
        :show-time="isDateTimeField"
        :get-popup-container="getPopupContainer"
        :popup-style="datePickerPopupStyle"
        @change="handleDateChange"
        @click.stop=""
      />

      <template
        v-else-if="
          isSelectFieldComputed ||
          isUserFieldComputed ||
          isMultiSelectField ||
          isBooleanFieldComputed
        "
      >
        <CustomInput
          v-if="!isBooleanFieldComputed"
          format="text"
          v-model="searchQuery"
          :placeholder="getSearchPlaceholder()"
          type="primary"
          :icon="imageNames.SEARCH_VARIANT1"
          size="size-4"
          alt="search_icon"
          @click.stop=""
          @keydown.stop="$event.stopPropagation()"
        />

        <div class="max-h-64 overflow-auto py-2">
          <template v-if="filteredOptions.length > 0">
            <template
              v-for="(option, idx) in filteredOptions"
              :key="option.value + '_' + idx"
            >
              <div
                class="dropdown_item flex items-center gap-2 px-2 py-2 cursor-pointer rounded"
                :class="{ selected: isSelected(option.value) }"
                @click.stop="toggleOption(option.value)"
              >
                <ThemeImage
                  v-if="isSelected(option.value)"
                  :icon="imageNames.TICK_GREY"
                  alt="tick_icon"
                  class="size-4"
                />
                <div v-else class="size-4 shrink-0" />
                <RemovedUserIcon v-if="isUserFieldComputed && option.isDeleted" />
                <ProfileIcon
                  v-else-if="isUserFieldComputed"
                  :data="{ id: option.value, name: option.label, avatar_color: option.avatar_color }"
                />
                <a-tooltip
                  placement="top"
                  :title="
                    getTooltipTitle(
                      'op1_' + option.value + '_' + idx,
                      option.label,
                    )
                  "
                  :overlay-style="{ zIndex: 10000 }"
                >
                  <div
                    class="truncate min-w-0"
                    :ref="
                      (el) =>
                        setContainerRef(el, 'op1_' + option.value + '_' + idx)
                    "
                  >
                    <span
                      :ref="
                        (el) => setTextRef(el, 'op1_' + option.value + '_' + idx)
                      "
                    >
                      <RemovedUserLabel
                        :name="option.label"
                        :is-deleted="option.isDeleted"
                      />
                    </span>
                  </div>
                </a-tooltip>
              </div>

              <div
                v-if="selectedFilterCount > 0 && idx === selectedFilterCount - 1 && selectedFilterCount < filteredOptions.length"
                class="h-px bg-[var(--white-opacity-8)] mx-3 my-1 shrink-0"
              ></div>
            </template>
          </template>
          <template v-else>
            <div class="flex flex-col items-center justify-center py-6">
              <ThemeImage
                :icon="imageNames.EMPTY_SCREEN_GREY"
                alt="empty_icon"
                class="size-12"
              />
              <span class="text-sm secondary_color mt-2">No results found</span>
            </div>
          </template>
        </div>
      </template>

      <a-input
        v-else
        v-model:value="localValue"
        :placeholder="getInputPlaceholder()"
        :type="isNumericFieldComputed ? 'number' : 'text'"
        allow-clear
        auto-focus
        @click.stop=""
        @keydown.stop="$event.stopPropagation()"
        @keydown.enter="handleSubmit"
        class="input_medium w-full"
      >
        <template v-if="attribute?.displayPrefix" #prefix>
          <span class="secondary_color text-sm">{{ attribute.displayPrefix.toUpperCase() }}-</span>
        </template>
      </a-input>
    </div>

    <div v-else-if="!showOperators" class="px-3 pb-3">
      <a-input
        v-if="isTextField"
        v-model:value="localValue"
        :placeholder="`Enter ${filterName.toLowerCase()}`"
        allow-clear
        auto-focus
        @click.stop=""
        @keydown.stop="$event.stopPropagation()"
        @keydown.enter="handleSimpleSubmit"
        class="input_medium w-full"
      />

      <template
        v-else-if="
          isSelectFieldComputed ||
          isUserFieldComputed ||
          isMultiSelectField ||
          isBooleanFieldComputed
        "
      >
        <CustomInput
          v-if="!isBooleanFieldComputed"
          format="text"
          v-model="searchQuery"
          :placeholder="getSearchPlaceholder()"
          type="primary"
          :icon="imageNames.SEARCH_VARIANT1"
          size="size-4"
          alt="search_icon"
          @click.stop=""
          @keydown.stop="$event.stopPropagation()"
        />

        <div class="max-h-64 overflow-auto py-2">
          <template v-if="filteredOptions.length > 0">
            <template
              v-for="(option, idx) in filteredOptions"
              :key="option.value + '_' + idx"
            >
              <div
                class="dropdown_item flex items-center gap-2 px-2 py-2 cursor-pointer rounded"
                :class="{ selected: isSelected(option.value) }"
                @click.stop="toggleOption(option.value)"
              >
                <ThemeImage
                  v-if="isSelected(option.value)"
                  :icon="imageNames.TICK_GREY"
                  alt="tick_icon"
                  class="size-4"
                />
                <div v-else class="size-4 shrink-0" />
                <RemovedUserIcon v-if="isUserFieldComputed && option.isDeleted" />
                <ProfileIcon
                  v-else-if="isUserFieldComputed"
                  :data="{ id: option.value, name: option.label, avatar_color: option.avatar_color }"
                />
                <a-tooltip
                  placement="top"
                  :title="
                    getTooltipTitle(
                      'op2_' + option.value + '_' + idx,
                      option.label,
                    )
                  "
                  :overlay-style="{ zIndex: 10000 }"
                >
                  <div
                    class="truncate min-w-0"
                    :ref="
                      (el) =>
                        setContainerRef(el, 'op2_' + option.value + '_' + idx)
                    "
                  >
                    <span
                      :ref="
                        (el) => setTextRef(el, 'op2_' + option.value + '_' + idx)
                      "
                    >
                      <RemovedUserLabel
                        :name="option.label"
                        :is-deleted="option.isDeleted"
                      />
                    </span>
                  </div>
                </a-tooltip>
              </div>

              <div
                v-if="selectedFilterCount > 0 && idx === selectedFilterCount - 1 && selectedFilterCount < filteredOptions.length"
                class="h-px bg-[var(--white-opacity-8)] mx-3 my-1 shrink-0"
              ></div>
            </template>
          </template>
          <template v-else>
            <div class="flex flex-col items-center justify-center py-6">
              <ThemeImage
                :icon="imageNames.EMPTY_SCREEN_GREY"
                alt="empty_icon"
                class="size-12"
              />
              <span class="text-sm secondary_color mt-2">No results found</span>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, nextTick } from "vue";
import dayjs from "dayjs";
import {
  getOperatorsForDataType,
  filterOperators,
  requiresValue,
} from "@/src/constants/analyticsFilterConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { sortOptionsSelectedFirst } from "@/src/utils/dropdownUtils.js";
import {
  currentUserId,
  booleanOptions,
  isNumericField,
  isDateField,
  isBooleanField as isBooleanFieldType,
  isSelectField as isSelectFieldType,
  isUserField as isUserFieldType,
  isTagsField,
} from "@/src/constants/crmConstants.js";
import { STRICTLY_NUMERIC_TYPES } from "@/src/constants/crmFilterConstants.js";
import { calculateDateRange } from "@/src/constants/filterConstants.js";
import CustomInput from "@/src/components/CustomInput.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import FieldDropdown from "@/src/components/task_components/FieldDropdown.vue";
import RecordTypeFilterInput from "./RecordTypeFilterInput.vue";
import {
  createDisabledEndDate,
  createDisabledEndTime,
} from "@/src/utils/dateTimeUtils.js";
import RemovedUserIcon from "@/src/components/RemovedUserIcon.vue";
import RemovedUserLabel from "@/src/components/RemovedUserLabel.vue";
import { useTruncationTooltip } from "@/src/composables/useTruncationTooltip.js";

const props = defineProps({
  filterId: { type: String, required: true },
  filterName: { type: String, required: true },
  fieldType: { type: String, default: "text" },
  currentValue: {
    type: [String, Number, Boolean, Array, Object],
    default: null,
  },
  options: { type: Array, default: () => [] },
  showOperators: { type: Boolean, default: true },
  useDatePresets: { type: Boolean, default: false },
  datePresetOptions: { type: Array, default: () => [] },
  attribute: { type: Object, default: () => ({}) },
  relatedObjects: { type: Array, default: () => [] },
  sortSelectedFirst: { type: Boolean, default: false },
});

const emit = defineEmits(["submit", "clear", "search", "custom-date"]);

const operatorDropdownRef = ref(null);
const recordTypeFilterRef = ref(null);
const { setTextRef, setContainerRef, shouldShowTooltip } =
  useTruncationTooltip();
// Reactive counter to trigger re-evaluation of shouldShowTooltip after refs are set
const tooltipRevision = ref(0);
const localValue = ref(null);
const localValue2 = ref(null);
const selectedOperator = ref(null);
const selectedValues = ref([]);
const searchQuery = ref("");

let searchTimeout = null;
watch(searchQuery, (newValue) => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    emit("search", { filterId: props.filterId, searchQuery: newValue });
  }, 300);
});

const datePickerPopupStyle = { zIndex: 10000 };

const getPopupContainer = () => {
  if (typeof window !== "undefined" && document?.body) {
    return document.body;
  }
  return undefined;
};

const isTextField = computed(() => {
  const type = props.fieldType?.toLowerCase();
  return (
    isNumericField(type) ||
    ["text", "email", "url", "phone_number"].includes(type)
  );
});

const isNumericFieldComputed = computed(() => {
  const type = props.fieldType?.toLowerCase();
  return STRICTLY_NUMERIC_TYPES.includes(type);
});

const isDateFieldComputed = computed(() => {
  return isDateField(props.fieldType);
});

const isDateTimeField = computed(() => {
  const type = props.fieldType?.toLowerCase();
  return ["datetime", "date_time", "dateandtime", "date_and_time"].includes(
    type,
  );
});

const disabledBetweenEndDate = createDisabledEndDate(() => localValue.value, {
  allowSameDay: () => isDateTimeField.value,
});
const disabledBetweenEndTime = createDisabledEndTime(() => localValue.value);
const disabledCustomEndDate = createDisabledEndDate(
  () => customDateValue.value,
  { allowSameDay: () => isDateTimeField.value },
);
const disabledCustomEndTime = createDisabledEndTime(
  () => customDateValue.value,
);

const isBooleanFieldComputed = computed(() => {
  return isBooleanFieldType(props.fieldType);
});

const isSelectFieldComputed = computed(() => {
  return isSelectFieldType(props.fieldType);
});

const isUserFieldComputed = computed(() => {
  return isUserFieldType(props.fieldType);
});

const isMultiSelectField = computed(() => {
  return isTagsField(props.fieldType);
});

const selectedDatePreset = ref(null);
const showCustomDatePicker = ref(false);
const customDateValue = ref(null);
const customDateValue2 = ref(null); // For "between" operator end date

// Reset end value when start value changes and becomes >= end value
// flush: sync ensures reset happens before the @change handler submits stale values
watch(
  localValue,
  (newStart) => {
    if (!newStart || !localValue2.value) return;

    if (typeof localValue2.value?.isAfter === "function") {
      // Date values — use dayjs comparison
      if (!localValue2.value.isAfter(newStart)) {
        localValue2.value = null;
      }
    } else if (!isNaN(Number(newStart)) && !isNaN(Number(localValue2.value))) {
      // Numeric values — use number comparison
      if (Number(localValue2.value) <= Number(newStart)) {
        localValue2.value = null;
      }
    }
  },
  { flush: "sync" },
);

// Reset end value when it changes and becomes <= start value (numeric between)
watch(
  localValue2,
  (newEnd) => {
    if (
      newEnd &&
      localValue.value &&
      !isNaN(Number(newEnd)) &&
      !isNaN(Number(localValue.value)) &&
      Number(newEnd) <= Number(localValue.value)
    ) {
      localValue2.value = null;
    }
  },
  { flush: "sync" },
);

watch(
  customDateValue,
  (newStart) => {
    if (
      newStart &&
      customDateValue2.value &&
      typeof customDateValue2.value?.isAfter === "function" &&
      !customDateValue2.value.isAfter(newStart)
    ) {
      customDateValue2.value = null;
    }
  },
  { flush: "sync" },
);

const isDatePresetSelected = (optionId) => {
  if (props.currentValue && typeof props.currentValue === "object") {
    return props.currentValue?.option === optionId;
  }
  return selectedDatePreset.value === optionId;
};

// Handle custom date selection from date picker
const handleCustomDateChange = (date) => {
  if (date) {
    // Format based on field type - include time for datetime fields
    const dateStr = isDateTimeField.value
      ? date.format("YYYY-MM-DD HH:mm:ss")
      : date.format("YYYY-MM-DD");
    selectedDatePreset.value = "custom";

    // Use the selected operator if showOperators is true, otherwise default to "equals"
    const operator =
      props.showOperators && selectedOperator.value
        ? selectedOperator.value
        : "equals";

    emit("submit", {
      filterId: props.filterId,
      value: {
        operator: operator,
        value: dateStr,
      },
    });
  }
};

// Handle custom date range selection for between/not_between operators
const handleCustomDateRangeChange = () => {
  // Only submit when both dates are selected
  if (customDateValue.value && customDateValue2.value) {
    const formatDate = (date) => {
      return isDateTimeField.value
        ? date.format("YYYY-MM-DD HH:mm:ss")
        : date.format("YYYY-MM-DD");
    };

    const startDateStr = formatDate(customDateValue.value);
    const endDateStr = formatDate(customDateValue2.value);
    selectedDatePreset.value = "custom";

    emit("submit", {
      filterId: props.filterId,
      value: {
        operator: selectedOperator.value,
        value: [startDateStr, endDateStr],
      },
    });
  }
};

// Handle back button to return to preset options
const handleBackToPresets = () => {
  showCustomDatePicker.value = false;
  customDateValue.value = null;
  customDateValue2.value = null;
};

// List of date preset IDs that are not actual date values
const datePresetIds = [
  "today",
  "yesterday",
  "last_7_days",
  "last_30_days",
  "this_month",
  "last_month",
  "custom",
];

// Handle date preset selection (presets or custom date picker)
const handleDatePresetClick = (option) => {
  if (option.id === "custom") {
    // Show the custom date picker inline
    showCustomDatePicker.value = true;

    // Initialize operator if showOperators is enabled
    if (props.showOperators && !selectedOperator.value) {
      const defaultOperators = getOperatorsForDataType(props.fieldType);
      selectedOperator.value = defaultOperators[0] || "equals";
    }

    // Initialize with current value if available
    if (props.currentValue?.value) {
      const currentVal = props.currentValue.value;
      // Check if it's a date range array (between operator)
      if (Array.isArray(currentVal) && currentVal.length >= 2) {
        const parsedStart = dayjs(currentVal[0]);
        const parsedEnd = dayjs(currentVal[1]);
        customDateValue.value = parsedStart.isValid() ? parsedStart : null;
        customDateValue2.value = parsedEnd.isValid() ? parsedEnd : null;
      } else if (!datePresetIds.includes(currentVal)) {
        // Single date value - check if it's an actual date (not a preset string)
        const parsedDate = dayjs(currentVal);
        if (parsedDate.isValid()) {
          customDateValue.value = parsedDate;
        } else {
          customDateValue.value = null;
        }
        customDateValue2.value = null;
      } else {
        customDateValue.value = null;
        customDateValue2.value = null;
      }
    }
  } else {
    selectedDatePreset.value = option.id;
    emit("submit", {
      filterId: props.filterId,
      value: {
        operator: "equals",
        value: option.id,
      },
    });
  }
};

const isRecordFieldComputed = computed(() => {
  const type = props.fieldType?.toLowerCase();
  return type === "relation_record";
});

const selectOptions = computed(() => {
  if (isBooleanFieldComputed.value) {
    return booleanOptions.map((option) => ({
      label: option.label,
      value: option.value,
    }));
  }

  return props.options.map((option) => ({
    label: option.name || option.label || option.value || String(option),
    value: option.id || option.value || option,
    isDeleted: option.isDeleted || false,
    avatar_color: option.avatar_color,
  }));
});

const sortedFilterData = computed(() => {
  const options = selectOptions.value;
  if (!props.sortSelectedFirst || !selectedValues.value || selectedValues.value.length === 0 || !options || options.length === 0) {
    return { sortedOptions: options || [], selectedCount: 0 };
  }
  return sortOptionsSelectedFirst(options, (option) => selectedValues.value.includes(option.value));
});

const filteredOptions = computed(() => sortedFilterData.value.sortedOptions);

const selectedFilterCount = computed(() => sortedFilterData.value.selectedCount);

// After options render, bump revision so tooltip titles re-evaluate with populated refs
watch(
  filteredOptions,
  () => {
    nextTick(() => {
      tooltipRevision.value++;
    });
  },
  { immediate: true },
);

const getTooltipTitle = (key, label) => {
  // Reference tooltipRevision to make this reactive - re-evaluated after refs are set
  void tooltipRevision.value;
  return shouldShowTooltip(key) ? label : "";
};

const operatorRequiresValue = (operatorId) => {
  return requiresValue(operatorId);
};

const operatorRequiresTwoValues = (operatorId) => {
  return operatorId === "between" || operatorId === "not_between";
};

const operatorOptions = computed(() => {
  if (!props.showOperators) return [];
  const operators = getOperatorsForDataType(props.fieldType);
  return filterOperators
    .filter((op) => operators.includes(op.id) && op.id !== "contains_all")
    .map((op) => ({
      id: op.id,
      name: op.label,
      label: op.label,
      value: op.id,
    }));
});

const showValueInput = computed(() => {
  if (!props.showOperators) return true;
  if (!selectedOperator.value) return true;
  return operatorRequiresValue(selectedOperator.value);
});

const showBetweenInputs = computed(() => {
  if (!props.showOperators) return false;
  return operatorRequiresTwoValues(selectedOperator.value);
});

const showDatePresetBetweenInputs = computed(() => {
  return operatorRequiresTwoValues(selectedOperator.value);
});

// Check if date preset custom mode should show date input (operator requires a value)
const showDatePresetDateInput = computed(() => {
  return operatorRequiresValue(selectedOperator.value);
});

const isOnlyMeSelected = computed(() => {
  return selectedValues.value.includes(currentUserId);
});

const getInputPlaceholder = () => {
  if (!selectedOperator.value) {
    return `Enter ${props.filterName.toLowerCase()}`;
  }
  const operator = filterOperators.find(
    (op) => op.id === selectedOperator.value,
  );
  return operator
    ? `Enter value for ${operator.label.toLowerCase()}`
    : `Enter ${props.filterName.toLowerCase()}`;
};

const getSearchPlaceholder = () => {
  if (isUserFieldComputed.value) return "Search owner";
  if (isMultiSelectField.value) return "Search values";
  return "Search values";
};

const isSelected = (value) => {
  return selectedValues.value.includes(value);
};

const getOptionLabel = (value) => {
  if (isUserFieldComputed.value && value === currentUserId) {
    return "Only Me";
  }
  const option = selectOptions.value.find((opt) => opt.value === value);
  return option ? option.label : value;
};

const toDayjsValue = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  return null;
};

const setLocalValue = (value, isSecondValue = false) => {
  const targetRef = isSecondValue ? localValue2 : localValue;
  if (isDateFieldComputed.value) {
    targetRef.value = toDayjsValue(value);
  } else {
    targetRef.value = value ?? "";
  }
};

watch(
  () => props.currentValue,
  (newValue) => {
    if (isRecordFieldComputed.value) {
      if (newValue && typeof newValue === "object" && newValue.operator) {
        selectedOperator.value = newValue.operator;
      } else if (!selectedOperator.value) {
        const defaultOperators = getOperatorsForDataType(props.fieldType);
        selectedOperator.value = defaultOperators[0] || "contains_any";
      }
      return;
    }

    // For date presets, auto-show custom date picker when value is a custom date (not a preset)
    if (
      props.useDatePresets &&
      newValue &&
      typeof newValue === "object" &&
      newValue.operator
    ) {
      selectedOperator.value = newValue.operator;

      // If operator doesn't require a value (is_empty, is_not_empty), show custom picker without date inputs
      if (!operatorRequiresValue(newValue.operator)) {
        showCustomDatePicker.value = true;
        customDateValue.value = null;
        customDateValue2.value = null;
        return;
      }

      // Check if it's a between operator with date range
      if (operatorRequiresTwoValues(newValue.operator)) {
        showCustomDatePicker.value = true;
        if (Array.isArray(newValue.value) && newValue.value.length >= 2) {
          const parsedStart = dayjs(newValue.value[0]);
          const parsedEnd = dayjs(newValue.value[1]);
          customDateValue.value = parsedStart.isValid() ? parsedStart : null;
          customDateValue2.value = parsedEnd.isValid() ? parsedEnd : null;
        }
        return;
      }

      // Check if value is a custom date (not a preset like "today", "yesterday", etc.)
      if (
        newValue.value &&
        typeof newValue.value === "string" &&
        !datePresetIds.includes(newValue.value)
      ) {
        const parsedDate = dayjs(newValue.value);
        if (parsedDate.isValid()) {
          showCustomDatePicker.value = true;
          customDateValue.value = parsedDate;
          customDateValue2.value = null;
          return;
        }
      }
    }

    if (!props.showOperators) {
      if (
        isSelectFieldComputed.value ||
        isUserFieldComputed.value ||
        isMultiSelectField.value ||
        isBooleanFieldComputed.value
      ) {
        selectedValues.value = Array.isArray(newValue)
          ? newValue
          : newValue !== null && newValue !== undefined
            ? [newValue]
            : [];
      } else {
        setLocalValue(newValue);
      }
      return;
    }

    if (newValue && typeof newValue === "object" && newValue.operator) {
      selectedOperator.value = newValue.operator;

      if (operatorRequiresTwoValues(newValue.operator)) {
        if (Array.isArray(newValue.value) && newValue.value.length >= 2) {
          setLocalValue(newValue.value[0]);
          setLocalValue(newValue.value[1], true);
        } else if (
          typeof newValue.value === "object" &&
          newValue.value !== null
        ) {
          setLocalValue(newValue.value.start);
          setLocalValue(newValue.value.end, true);
        } else {
          setLocalValue(null);
          setLocalValue(null, true);
        }
      } else if (operatorRequiresValue(newValue.operator)) {
        if (
          isSelectFieldComputed.value ||
          isUserFieldComputed.value ||
          isMultiSelectField.value ||
          isBooleanFieldComputed.value
        ) {
          selectedValues.value = Array.isArray(newValue.value)
            ? newValue.value
            : newValue.value !== null && newValue.value !== undefined
              ? [newValue.value]
              : [];
        } else {
          setLocalValue(newValue.value);
        }
        setLocalValue(null, true);
      } else {
        if (
          isSelectFieldComputed.value ||
          isUserFieldComputed.value ||
          isMultiSelectField.value ||
          isBooleanFieldComputed.value
        ) {
          selectedValues.value = [];
        } else {
          setLocalValue(null);
        }
        setLocalValue(null, true);
      }
    } else {
      const defaultOperators = getOperatorsForDataType(props.fieldType);
      selectedOperator.value = defaultOperators[0] || "equals";

      if (
        isSelectFieldComputed.value ||
        isUserFieldComputed.value ||
        isMultiSelectField.value ||
        isBooleanFieldComputed.value
      ) {
        selectedValues.value = Array.isArray(newValue)
          ? newValue
          : newValue !== null && newValue !== undefined
            ? [newValue]
            : [];
      } else {
        setLocalValue(newValue);
      }
      setLocalValue(null, true);
    }
  },
  { immediate: true },
);

const handleOperatorChange = () => {
  nextTick(() => {
    if (operatorDropdownRef.value?.closeDropdown) {
      operatorDropdownRef.value.closeDropdown();
    }
  });

  if (isRecordFieldComputed.value) {
    return;
  }

  // For date presets, clear custom date values when operator changes
  if (props.useDatePresets) {
    customDateValue.value = null;
    customDateValue2.value = null;
  }

  if (!operatorRequiresValue(selectedOperator.value)) {
    if (
      isSelectFieldComputed.value ||
      isUserFieldComputed.value ||
      isMultiSelectField.value ||
      isBooleanFieldComputed.value
    ) {
      selectedValues.value = [];
    } else {
      localValue.value = null;
      localValue2.value = null;
    }
    handleSubmit();
  }
};

const handleDateChange = () => {
  handleSubmit();
};

const toggleOnlyMe = () => {
  const index = selectedValues.value.indexOf(currentUserId);
  if (index > -1) {
    selectedValues.value.splice(index, 1);
  } else {
    selectedValues.value.push(currentUserId);
  }
  if (props.showOperators) {
    handleSubmit();
  } else {
    handleSimpleSubmit();
  }
};

const toggleOption = (value) => {
  const index = selectedValues.value.indexOf(value);
  if (index > -1) {
    selectedValues.value.splice(index, 1);
  } else {
    selectedValues.value.push(value);
  }
};

const removeSelectedValue = (value) => {
  const index = selectedValues.value.indexOf(value);
  if (index > -1) {
    selectedValues.value.splice(index, 1);
    if (props.showOperators) {
      handleSubmit();
    } else {
      handleSimpleSubmit();
    }
  }
};

const handleSimpleSubmit = () => {
  try {
    if (
      isSelectFieldComputed.value ||
      isUserFieldComputed.value ||
      isMultiSelectField.value ||
      isBooleanFieldComputed.value
    ) {
      if (selectedValues.value.length > 0) {
        emit("submit", {
          filterId: props.filterId,
          value: selectedValues.value,
        });
      } else {
        emit("clear", { filterId: props.filterId });
      }
    } else {
      const trimmed = (localValue.value || "").toString().trim();
      if (trimmed) {
        emit("submit", {
          filterId: props.filterId,
          value: trimmed,
        });
      } else {
        emit("clear", { filterId: props.filterId });
      }
    }
  } catch (error) {
    console.error("Error in handleSimpleSubmit:", error);
  }
};

const handleSubmit = () => {
  if (!props.showOperators) {
    handleSimpleSubmit();
    return;
  }

  try {
    if (!selectedOperator.value) {
      const defaultOperators = getOperatorsForDataType(props.fieldType);
      selectedOperator.value = defaultOperators[0] || "equals";
    }

    if (!operatorRequiresValue(selectedOperator.value)) {
      emit("submit", {
        filterId: props.filterId,
        value: {
          operator: selectedOperator.value,
          value: null,
        },
      });
      return;
    }

    if (operatorRequiresTwoValues(selectedOperator.value)) {
      const formatValue = (val) => {
        if (!val) return "";
        if (val && typeof val === "object" && val.format) {
          return isDateTimeField.value
            ? val.format("YYYY-MM-DD HH:mm:ss")
            : val.format("YYYY-MM-DD");
        }
        return String(val).trim();
      };

      const value1 = formatValue(localValue.value);
      const value2 = formatValue(localValue2.value);

      if (value1 && value2) {
        emit("submit", {
          filterId: props.filterId,
          value: {
            operator: selectedOperator.value,
            value: {
              start: value1,
              end: value2,
            },
          },
        });
      } else if (!value1 && !value2) {
        emit("clear", { filterId: props.filterId });
      }
      return;
    }

    if (
      isSelectFieldComputed.value ||
      isUserFieldComputed.value ||
      isMultiSelectField.value ||
      isBooleanFieldComputed.value
    ) {
      if (selectedValues.value.length > 0) {
        emit("submit", {
          filterId: props.filterId,
          value: {
            operator: selectedOperator.value,
            value: selectedValues.value,
          },
        });
      } else {
        emit("clear", { filterId: props.filterId });
      }
    } else {
      let valueToSubmit = localValue.value;
      if (
        valueToSubmit &&
        typeof valueToSubmit === "object" &&
        valueToSubmit.format
      ) {
        valueToSubmit = isDateTimeField.value
          ? valueToSubmit.format("YYYY-MM-DD HH:mm:ss")
          : valueToSubmit.format("YYYY-MM-DD");
      }
      const trimmed = (valueToSubmit || "").toString().trim();

      if (trimmed) {
        emit("submit", {
          filterId: props.filterId,
          value: {
            operator: selectedOperator.value,
            value: trimmed,
          },
        });
      } else {
        emit("clear", { filterId: props.filterId });
      }
    }
  } catch (error) {
    console.error("Error in handleSubmit:", error);
  }
};

const buildSubmitData = () => {
  if (props.useDatePresets) {
    return undefined;
  }

  if (isRecordFieldComputed.value) {
    return undefined;
  }

  if (!props.showOperators) {
    if (
      isSelectFieldComputed.value ||
      isUserFieldComputed.value ||
      isMultiSelectField.value ||
      isBooleanFieldComputed.value
    ) {
      if (selectedValues.value.length > 0) {
        return { filterId: props.filterId, value: selectedValues.value };
      }
    } else {
      let valueToSubmit = localValue.value;
      if (
        valueToSubmit &&
        typeof valueToSubmit === "object" &&
        valueToSubmit.format
      ) {
        valueToSubmit = isDateTimeField.value
          ? valueToSubmit.format("YYYY-MM-DD HH:mm:ss")
          : valueToSubmit.format("YYYY-MM-DD");
      }
      const trimmed = (valueToSubmit || "").toString().trim();
      if (trimmed) {
        return { filterId: props.filterId, value: trimmed };
      }
    }
    return null;
  }

  // With operators
  try {
    let operator = selectedOperator.value;
    if (!operator) {
      const defaultOperators = getOperatorsForDataType(props.fieldType);
      operator = defaultOperators[0] || "equals";
    }

    if (!operatorRequiresValue(operator)) {
      return {
        filterId: props.filterId,
        value: { operator, value: null },
      };
    }

    if (operatorRequiresTwoValues(operator)) {
      const formatValue = (val) => {
        if (!val) return null;
        if (typeof val === "object" && val.format) {
          return isDateTimeField.value
            ? val.format("YYYY-MM-DD HH:mm:ss")
            : val.format("YYYY-MM-DD");
        }
        return String(val).trim();
      };

      const value1 = formatValue(localValue.value);
      const value2 = formatValue(localValue2.value);

      if (value1 && value2) {
        return {
          filterId: props.filterId,
          value: {
            operator,
            value: { start: value1, end: value2 },
          },
        };
      }
      return null;
    }

    if (
      isSelectFieldComputed.value ||
      isUserFieldComputed.value ||
      isMultiSelectField.value
    ) {
      if (selectedValues.value.length > 0) {
        return {
          filterId: props.filterId,
          value: { operator, value: selectedValues.value },
        };
      }
    } else if (
      isSelectFieldComputed.value ||
      isUserFieldComputed.value ||
      isMultiSelectField.value ||
      isBooleanFieldComputed.value
    ) {
      if (selectedValues.value.length > 0) {
        return {
          filterId: props.filterId,
          value: { operator, value: selectedValues.value },
        };
      }
    } else {
      let valueToSubmit = localValue.value;
      if (
        valueToSubmit &&
        typeof valueToSubmit === "object" &&
        valueToSubmit.format
      ) {
        valueToSubmit = isDateTimeField.value
          ? valueToSubmit.format("YYYY-MM-DD HH:mm:ss")
          : valueToSubmit.format("YYYY-MM-DD");
      }
      const trimmed = (valueToSubmit || "").toString().trim();

      if (trimmed) {
        return {
          filterId: props.filterId,
          value: { operator, value: trimmed },
        };
      }
    }
  } catch (error) {
    console.error("Error in buildSubmitData:", error);
  }

  return null;
};

defineExpose({
  submit: () => {
    if (isRecordFieldComputed.value && recordTypeFilterRef.value?.submit) {
      return recordTypeFilterRef.value.submit();
    }

    const data = buildSubmitData();

    // Return data without emitting - let the caller handle the submission
    // This prevents duplicate events when called programmatically
    return data;
  },
});
</script>
